import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiMapPinLine, RiEditLine, RiCloseLine, RiSaveLine, RiLoader4Line } from 'react-icons/ri';
import { API_BASE_URL } from '../../config';

const ManageDistricts = () => {
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingDistrict, setEditingDistrict] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchDistricts();
    }, []);

    const fetchDistricts = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/districts/read_all.php`);
            const data = await response.json();
            if (response.ok) {
                setDistricts(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching districts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/districts/update.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingDistrict)
            });
            if (response.ok) {
                setEditingDistrict(null);
                fetchDistricts();
            }
        } catch (error) {
            console.error('Error updating district:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Manage Regional Districts</h1>
                    <p className="text-slate-500">Update district descriptions and metadata for the public travel section.</p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <RiLoader4Line className="animate-spin text-emerald-500 text-3xl" />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {districts.map((district) => (
                        <motion.div
                            key={district.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                                    <RiMapPinLine size={24} />
                                </div>
                                <button
                                    onClick={() => setEditingDistrict(district)}
                                    className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-emerald-600 transition-colors"
                                >
                                    <RiEditLine size={20} />
                                </button>
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">{district.name}</h3>
                            <p className="text-sm text-slate-500 line-clamp-3 mb-4 leading-relaxed font-medium">
                                {district.description}
                            </p>
                            <div className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
                                Slug: {district.slug}
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Edit Modal */}
            <AnimatePresence>
                {editingDistrict && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden"
                        >
                            <div className="flex justify-between items-center p-8 border-b border-slate-100">
                                <h2 className="text-xl font-bold text-slate-900">Edit {editingDistrict.name}</h2>
                                <button
                                    onClick={() => setEditingDistrict(null)}
                                    className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors"
                                >
                                    <RiCloseLine size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleUpdate} className="p-8 space-y-6">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">District Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={editingDistrict.name}
                                        onChange={(e) => setEditingDistrict({ ...editingDistrict, name: e.target.value })}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Description</label>
                                    <textarea
                                        required
                                        rows={4}
                                        value={editingDistrict.description}
                                        onChange={(e) => setEditingDistrict({ ...editingDistrict, description: e.target.value })}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Hero Image URL</label>
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                required
                                                value={editingDistrict.image_url}
                                                onChange={(e) => setEditingDistrict({ ...editingDistrict, image_url: e.target.value })}
                                                className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                            />
                                        </div>
                                        <div className="w-24 h-14 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
                                            <img src={editingDistrict.image_url} alt="Preview" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setEditingDistrict(null)}
                                        className="px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-emerald-600 transition-all flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {saving ? <RiLoader4Line className="animate-spin" /> : <RiSaveLine />}
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ManageDistricts;
